import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, mockArticles } from '@/mocks/articles';

function getCategoryLabel(slug: string): string {
  const map: Record<string, string> = {
    'japan-drama': '日剧',
    'korea-drama': '韩剧',
    'japan-movie': '日本电影',
    'korea-movie': '韩国电影',
    'kpop': 'K-POP',
    'jpop': 'J-POP',
    'variety': '综艺',
    'star': '明星',
  };
  return map[slug] || slug;
}

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'japan-drama');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeSlug);
  const categoryArticles = mockArticles.filter((a) => a.category === activeSlug);
  const categoryName = currentCategory?.name || getCategoryLabel(activeSlug);

  useEffect(() => {
    const pageSchema = {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: `${categoryName} - aaa一级日韩`,
      description: `浏览aaa一级日韩的${categoryName}相关内容，包括最新资讯、推荐和深度分析。`,
      url: `https://aaa-rikhan.example.com/category/${activeSlug}`,
      isPartOf: {
        '@type': 'WebSite',
        name: 'aaa一级日韩',
        url: 'https://aaa-rikhan.example.com/',
      },
    };

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: '首页',
          item: 'https://aaa-rikhan.example.com/',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: categoryName,
        },
      ],
    };

    const scripts = [JSON.stringify(pageSchema), JSON.stringify(breadcrumbSchema)];
    scripts.forEach((s) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = s;
      document.head.appendChild(script);
    });

    return () => {
      document.head.querySelectorAll('script[type="application/ld+json"]').forEach((el) => {
        if (scripts.some((s) => el.textContent === s)) el.remove();
      });
    };
  }, [activeSlug, categoryName]);

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-8 md:py-10">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-4 flex-wrap">
              <Link to="/" className="hover:text-primary-600 transition-colors duration-200 cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-[10px]"></i>
              <span className="text-foreground-700 font-medium">{categoryName}</span>
            </nav>
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
              {categoryName}
            </h1>
            {currentCategory && (
              <p className="mt-2 text-sm text-foreground-600">{currentCategory.description}</p>
            )}
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-8">
          <div className="flex items-center gap-2 flex-wrap mb-8">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-xs font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                  activeSlug === cat.slug
                    ? 'bg-foreground-950 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {categoryArticles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {categoryArticles.map((article) => (
                <article key={article.id} className="group cursor-pointer">
                  <Link to={`/article/${article.id}`} className="block">
                    <div className="relative overflow-hidden rounded-lg aspect-[16/10] mb-4">
                      <img
                        src={article.coverImage}
                        alt={article.title}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                    <h3 className="text-sm md:text-base font-heading font-semibold text-foreground-950 mb-2 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2">
                      {article.title}
                    </h3>
                    <p className="text-xs text-foreground-600 line-clamp-2 mb-3 leading-relaxed">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-foreground-500">
                      <span>{article.author}</span>
                      <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
                      <span>{article.publishedAt}</span>
                      <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
                      <span>{article.readTime}</span>
                    </div>
                  </Link>
                </article>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-inbox-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-sm text-foreground-500">暂无该分类的文章</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}